/**
 * Medical On Demand Form Hook
 *
 * Manages form state, validation, and submission for MOD inquiries.
 * Uses existing createInquiryV2 infrastructure with Product Support expert pre-selected.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useState, useCallback, useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ERROR_MESSAGES } from '@/constants/errorMessages';
import {
  getProductData,
  getSupportTeamByProductId,
  MOD_ROUTE_IDS,
  TERRITORY_ROUTES,
  PRODUCT_IDS,
  type ModRoute,
  type ProductId,
} from '@/constants/medicalOnDemand';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { useToaster } from '@/hooks/useToaster';
import { createInquiryV2, type CreateInquiryV2Body } from '@/services/createInquiryV2';
import { createMODInquiryMessageData } from '@/types/messageData';
import { segmentClient } from '@/util/analytics';
import { Sentry } from '@/util/sentry';
import { formatMODMessage } from '@/utils/modHelpers';

/** Maximum character length for MOD inquiry messages */
const MOD_MESSAGE_MAX_LENGTH = 5000;

/** US zip code: 5 digits, optional 4-digit extension. */
const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;

const baseFormObject = z.object({
  productIds: z
    .array(z.enum(PRODUCT_IDS))
    .min(1, { message: 'Please select at least one product' }),
  questionType: z.string().optional(),
  message: z
    .string()
    .min(1, { message: 'Please enter your question' })
    .max(MOD_MESSAGE_MAX_LENGTH, {
      message: `Question must be less than ${MOD_MESSAGE_MAX_LENGTH} characters`,
    }),
  routes: z.array(z.enum(MOD_ROUTE_IDS)).default([]),
  postalCode: z.string().optional(),
  complianceAcknowledged: z.literal(true, {
    errorMap: () => ({ message: 'Please acknowledge the compliance terms' }),
  }),
});

const refineTerritoryPostalCode = (
  data: z.infer<typeof baseFormObject>,
  ctx: z.RefinementCtx
): void => {
  if (!data.routes.some((route) => TERRITORY_ROUTES.includes(route))) {
    return;
  }

  const postalCode = (data.postalCode ?? '').trim();

  if (postalCode.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['postalCode'],
      message: 'Please enter your practice zip code',
    });
  } else if (!ZIP_CODE_REGEX.test(postalCode)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['postalCode'],
      message: 'Please enter a valid US zip code',
    });
  }
};

const modFormSchema = baseFormObject
  .extend({
    questionType: z.string().min(1, { message: 'Please select a question type' }),
  })
  .superRefine(refineTerritoryPostalCode);

const modFormSchemaOptionalQuestionType = baseFormObject.superRefine(refineTerritoryPostalCode);

export type MODFormData = z.infer<typeof modFormSchema>;

interface UseMODInquiryFormProps {
  onSuccess?: () => void;
  requireQuestionType?: boolean;
  /**
   * Prefilled into the MSL zip code field. Surfaced when the submitter selects
   * MSL as a routing destination so they don't have to retype it. Falls back to
   * empty string when the submitter has no postal code on file.
   */
  defaultPostalCode?: string | null;
}

/**
 * Form hook for Medical On Demand inquiries
 *
 * Handles form state, validation, and submission to Product Support experts.
 * Question type is captured as a prefix in the message field.
 */
export function useMODInquiryForm({
  onSuccess,
  requireQuestionType = true,
  defaultPostalCode,
}: UseMODInquiryFormProps = {}) {
  const toast = useToaster();

  const [submissionStatus, setSubmissionStatus] = useState<string>('');

  const hasTrackedFormStart = useRef(false);

  const schema = requireQuestionType ? modFormSchema : modFormSchemaOptionalQuestionType;

  const form = useForm<MODFormData>({
    defaultValues: {
      productIds: [],
      questionType: '',
      message: '',
      routes: [],
      postalCode: defaultPostalCode ?? '',
      complianceAcknowledged: false as unknown as true,
    },
    resolver: zodResolver(schema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    getValues,
  } = form;

  const formValues = useWatch({ control });

  /**
   * The submitter's profile postal code is fetched separately and may resolve
   * after the form mounts. When it arrives, prefill the MSL field — but only
   * if the submitter hasn't typed anything in yet, so we never clobber manual
   * edits.
   */
  useEffect(() => {
    if (!defaultPostalCode) {
      return;
    }

    if (!getValues('postalCode')) {
      setValue('postalCode', defaultPostalCode, { shouldDirty: false });
    }
  }, [defaultPostalCode, getValues, setValue]);

  const selectedProduct = formValues.productIds?.[0]
    ? getProductData(formValues.productIds[0])
    : null;

  useEffect(() => {
    if (!hasTrackedFormStart.current) {
      segmentClient.track(ANALYTICS_EVENTS.MOD_FORM_STARTED);

      hasTrackedFormStart.current = true;
    }
  }, []);

  useEffect(() => {
    if (formValues.productIds?.length) {
      segmentClient.track(ANALYTICS_EVENTS.MOD_PRODUCT_SELECTED, {
        product_ids: formValues.productIds,
        product_names: formValues.productIds.map((id) => getProductData(id)?.brandName),
      });
    }
  }, [formValues.productIds]);

  useEffect(() => {
    if (formValues.questionType) {
      segmentClient.track(ANALYTICS_EVENTS.MOD_QUESTION_TYPE_SELECTED, {
        question_type: formValues.questionType,
      });
    }
  }, [formValues.questionType]);

  const createInquiryMutation = useMutation({
    mutationFn: createInquiryV2,
    onSuccess: (_, variables) => {
      setSubmissionStatus('');

      segmentClient.track(ANALYTICS_EVENTS.MOD_FORM_SUBMITTED, {
        product_ids: formValues.productIds,
        product_count: formValues.productIds?.length ?? 0,
        question_type: formValues.questionType,
        expert_id: variables.expertId,
        routes: formValues.routes ?? null,
        route_count: formValues.routes?.length ?? 0,
        direct_to_msl: formValues.routes?.includes('msl') ?? false,
      });

      toast.showSuccess({ title: 'Question submitted successfully' });

      reset();

      onSuccess?.();
    },
    onError: (error) => {
      setSubmissionStatus('');

      segmentClient.track(ANALYTICS_EVENTS.MOD_FORM_SUBMIT_FAILED, {
        product_ids: formValues.productIds,
        product_count: formValues.productIds?.length ?? 0,
        question_type: formValues.questionType,
        routes: formValues.routes ?? null,
        route_count: formValues.routes?.length ?? 0,
        direct_to_msl: formValues.routes?.includes('msl') ?? false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      toast.showError({
        title: 'Unable to submit question',
        message:
          'Please check your connection and try again. If the problem persists, contact support.',
      });
    },
  });

  const isMaintenanceMode = useMaintenanceMode();

  const submitInquiry = handleSubmit(async (data) => {
    if (isMaintenanceMode) {
      toast.showError(ERROR_MESSAGES.INQUIRY.MAINTENANCE_MODE);

      return;
    }

    try {
      const productData = getProductData(data.productIds[0]);

      if (!productData) {
        toast.showError({
          title: 'Product not found',
          message: 'The selected product is not available. Please select a different product.',
        });

        return;
      }

      const supportTeam = getSupportTeamByProductId(productData.id);

      if (!supportTeam) {
        toast.showError({
          title: 'Support team not found',
          message: 'Unable to find the support team for this product. Please try again.',
        });

        return;
      }

      setSubmissionStatus('Submitting question...');

      const formattedMessage = formatMODMessage(data.productIds, data.questionType, data.message);

      /*
        An empty selection sends no routes at all, which the API turns into a
        single inquiry to the MOD expert for a human to triage. Nothing infers
        a route from the message, despite what the form's copy has claimed.
      */
      const routes = data.routes.length > 0 ? (data.routes as ModRoute[]) : undefined;

      const postalCode = routes?.some((route) => TERRITORY_ROUTES.includes(route))
        ? data.postalCode?.trim() || undefined
        : undefined;

      createInquiryMutation.mutate({
        cancerFocusId: supportTeam.cancerFocusId,
        message: formattedMessage,
        expertIds: [supportTeam.expertId],
        expertId: supportTeam.expertId,
        anonymous: false,
        anonymousFanOut: false,
        noPII: data.complianceAcknowledged,
        data: createMODInquiryMessageData(data.productIds, data.questionType),
        // The generated contract is one route behind: MIL fails validation
        // until the API accepts it.
        routes: routes as CreateInquiryV2Body['routes'],
        postalCode,
      });
    } catch (error) {
      Sentry.captureException(error, {
        extra: { productIds: data.productIds, questionType: data.questionType },
      });

      toast.showError({
        title: 'Submission failed',
        message: 'An unexpected error occurred. Please try again.',
      });
    }
  });

  const isSubmitting = createInquiryMutation.isPending || !!submissionStatus;

  const canSubmit = useCallback(() => {
    const hasRequiredFields =
      formValues.productIds?.length &&
      formValues.message &&
      formValues.complianceAcknowledged &&
      !isSubmitting;

    if (requireQuestionType) {
      return hasRequiredFields && !!formValues.questionType;
    }

    return hasRequiredFields;
  }, [formValues, isSubmitting, requireQuestionType]);

  return {
    control,
    errors,
    formValues,
    setValue,
    selectedProduct,
    submitInquiry,
    isSubmitting,
    submissionStatus,
    canSubmit,
    reset,
  };
}
